import { useContext, useState, useRef, useEffect } from "react"
import axios from "axios"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { Button } from "primereact/button"
import { RadioButton } from "primereact/radiobutton"
import { Toast } from "primereact/toast"
import EditorField from "../Utils/EditorField.js"
import styles from "../../../styles/Participant-Employment.module.css"
import { UserContext } from "../../../context/userContext"
import { Actions, showError } from "../Utils/Utils"
import { FollowAlongSupportTypes, Statuses } from "../Types/Dropdowns"

import { employmentApiUrl } from "../../../config/microservicesUrls"

const api_url = employmentApiUrl()

export default function FollowAlongSupportForm({
  closeForm,
  action,
  followAlongSupportRecord,
  jobSearchPlanId,
  updateData,
}) {
  const toast = useRef(null)

  const userCtx = useContext(UserContext)

  const [loading, setLoading] = useState(false)

  const [formErrors, setFormErrors] = useState(null)

  const defaultData = {
    providerOfSupport: "",
    supportType: undefined,
    otherTypeOfSupport: undefined,
    area: "",
    status: Statuses[0],
  }

  const [data, setData] = useState(defaultData)

  useEffect(() => {
    if (followAlongSupportRecord !== null) {
      const supportType = followAlongSupportRecord.supportType
        ? FollowAlongSupportTypes[followAlongSupportRecord.supportType]
        : undefined
      setData({
        ...followAlongSupportRecord,
        supportType: supportType,
        status: Statuses[followAlongSupportRecord.status],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearForm = () => {
    setData(defaultData)
    setFormErrors(null)
  }

  const handleSave = async () => {
    setLoading(true)
    const supportType = data.supportType
      ? FollowAlongSupportTypes.indexOf(data.supportType)
      : undefined
    const status = Statuses.indexOf(data.status)

    if (action === Actions.Add) {
      const createdData = {
        createdBy: userCtx.session.user.staff_id,
        ...data,
        supportType,
        status,
        jobSearchPlanId,
      }
      axios
        .post(`${api_url}/followAlongSupport`, createdData, {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getEmploymentToken()}`,
          },
        })
        .then(function (response) {
          clearForm()
          updateData(response.data)
          setLoading(false)
          closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to create follow along support", errors)
        })
    } else {
      const updatedData = {
        ...data,
        updatedBy: userCtx.session.user.staff_id,
        supportType,
        status,
        jobSearchPlanId,
      }
      axios
        .put(`${api_url}/followAlongSupport`, updatedData, {
          headers: {
            Authorization: `Bearer ${userCtx.tokens.getEmploymentToken()}`,
          },
        })
        .then(function (response) {
          clearForm()
          updateData(response.data)
          setLoading(false)
          closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to update follow along support", errors)
        })
    }
  }

  const renderFormErrors = () => {
    if (formErrors !== null) {
      return (
        <ul className={styles.errors_list}>
          {formErrors?.map((error, index) => {
            return (
              <li key={index}>
                <small className="p-error">{error}</small>
              </li>
            )
          })}
        </ul>
      )
    }
    return null
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="main-content">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h1 className={styles.view_title}>
            {action === Actions.Add
              ? "Add follow along support"
              : action === Actions.Edit
                ? "Edit follow along support"
                : "Follow along support"}
          </h1>
          <Button
            className={styles.rounded_btn}
            icon="pi pi-times"
            type="button"
            disabled={loading}
            onClick={() => {
              clearForm()
              closeForm()
            }}
          />
        </div>
        <div className={"card flex flex-wrap p-fluid " + styles.wrapper}>
          <h3 className={styles.view_subtitle}>Follow along support</h3>
          <hr className={styles.line}></hr>

          <div className={styles.field}>
            <label className={styles.label}>Provider(s) of support</label>
            <InputText
              value={data.providerOfSupport}
              onChange={(e) =>
                setData({ ...data, providerOfSupport: e.target.value })
              }
              className={"p-inputtext-md"}
              disabled={action === Actions.View}
              name="providerOfSupport"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Type of follow-along support</label>
            <div style={{ display: "flex", gap: "10px" }}>
              <Dropdown
                value={data.supportType}
                options={FollowAlongSupportTypes}
                panelClassName="custom_dropdown_panel"
                onChange={(e) => setData({ ...data, supportType: e.value })}
                placeholder="None"
                style={{ marginTop: "0", height: "32px", alignItems: "center" }}
                disabled={action === Actions.View}
              />
              {data.supportType === FollowAlongSupportTypes[8] && ( //Other
                <InputText
                  className="p-inputtext-md"
                  style={{ width: "100%" }}
                  placeholder={"Other: please specify here"}
                  value={data.otherTypeOfSupport}
                  onChange={(e) =>
                    setData({ ...data, otherTypeOfSupport: e.target.value })
                  }
                  name="otherTypeOfSupport"
                />
              )}
            </div>
          </div>

          <EditorField
            value={data.area}
            onTextChange={(e) => setData({ ...data, area: e })}
            label="Support area"
            disabled={action === Actions.View}
          />

          <div className={styles.field}>
            <label className={styles.label}>Support status</label>
            <div className={styles.radio_btn_wrapper}>
              {Statuses.map((status, index) => {
                return (
                  <div className={styles.radio_btn}>
                    <RadioButton
                      key={`status${index}`}
                      value={status}
                      style={{ margin: "0" }}
                      onChange={(e) => setData({ ...data, status: e.value })}
                      disabled={action === Actions.View}
                      checked={data.status === status}
                    />
                    <label
                      htmlFor={`status${index}`}
                      className={styles.inline_label}
                    >
                      {status}
                    </label>
                  </div>
                )
              })}
            </div>
          </div>

          <br />
          <div
            style={{ display: "flex", gap: "20px", justifyContent: "flex-end" }}
          >
            <Button
              className={styles.cancel_button}
              type="button"
              disabled={loading}
              onClick={() => {
                clearForm()
                closeForm()
              }}
            >
              {action === Actions.View ? "Back" : "Cancel"}
            </Button>
            {action !== Actions.View && (
              <Button
                className={styles.button}
                type="submit"
                disabled={loading}
                icon={loading ? "pi pi-spin pi-spinner" : ""}
                iconPos="right"
                label={`${
                  action === Actions.Add ? "Create" : "Update"
                } follow along support`}
                onClick={() => handleSave()}
              />
            )}
          </div>
          {renderFormErrors()}
        </div>
      </div>
    </>
  )
}
